import { camelCase } from 'change-case'

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Object.prototype.toString.call(value) === '[object Object]'
}

/**
 * Keys that must never be copied onto the result object: `JSON.parse` keeps
 * them as own enumerable properties, and assigning `__proto__` on a plain
 * object literal rewires its prototype (an attacker-supplied payload could
 * smuggle getters onto Object.prototype through the axios interceptors).
 */
const UNSAFE_KEYS = new Set(['__proto__', 'constructor', 'prototype'])

function transformKey(key: string): string {
  if (key === key.toUpperCase() && key.includes('_')) {
    return key.toLowerCase().replace(/_([a-z])/g, (_, c) => c.toUpperCase())
  }
  return camelCase(key)
}

export function toCamelCase<T = unknown>(data: unknown): T {
  if (Array.isArray(data)) {
    return data.map((item) => toCamelCase(item)) as T
  }

  if (isPlainObject(data)) {
    const result: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(data)) {
      const camelKey = transformKey(key)
      if (UNSAFE_KEYS.has(key) || UNSAFE_KEYS.has(camelKey)) continue
      result[camelKey] = toCamelCase(value)
    }
    return result as T
  }

  return data as T
}

export function toSnakeCase<T = unknown>(data: unknown): T {
  if (Array.isArray(data)) {
    return data.map((item) => toSnakeCase(item)) as T
  }

  if (isPlainObject(data)) {
    const result: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(data)) {
      if (UNSAFE_KEYS.has(key)) continue
      const snakeKey = key
        .replace(/([A-Z])/g, '_$1')
        .toLowerCase()
        .replace(/^_/, '')
      if (UNSAFE_KEYS.has(snakeKey)) continue
      result[snakeKey] = toSnakeCase(value)
    }
    return result as T
  }

  return data as T
}
